import { Box, Collapse, Stack, Typography, styled } from '@mui/material'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { getCostPrice, selectCostPriceById, selectCostPriceStatus } from '../costprice.slice'
import { useAppDispatch } from '../../../store/rootStore'
import MaterialSymbol from '../../../components/MaterialSymbol'
import { colors } from '../../../themes/colors'
import { formatPrice } from '../../../util/helpers'
import { Loader } from '../../../components/Loader'
import withTfoAccess from '../../../util/withTfoAccess'
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage'
import { CostPrice } from '../costprice.types'
import { debounce } from 'lodash'

const CostPriceButton = styled(Stack)(() => ({
  '&:hover': {
    cursor: 'pointer',
  },
}))

export interface CostPriceListProps {
  productId: number
}

const CostPriceListComponent: React.FC<CostPriceListProps> = (props) => {
  const dispatch = useAppDispatch()
  const costPrices = useSelector(selectCostPriceById(props.productId))
  const costPriceStatus = useSelector(selectCostPriceStatus(props.productId))
  const account = useSelector(selectCurrentFarmerAccount)

  const [expanded, setExpanded] = React.useState(false)
  useEffect(() => {
    if (account) {
      const debouncedFunc = debounce(
        () =>
          dispatch(
            getCostPrice({ productId: props.productId, accountNumber: account.accountNumber }),
          ),
        200,
      )

      debouncedFunc()
    }
  }, [account, props])

  const getLabel = (breakpoint: CostPrice) => {
    if (breakpoint.quantityTo === -1) {
      return 'All units'
    }

    const ToLabel = breakpoint.quantityTo === 0 ? 'Any' : breakpoint.quantityTo
    return `${breakpoint.quantityFrom}-${ToLabel} ${breakpoint.unit}`
  }

  const memo = React.useMemo(() => {
    if (!costPrices) {
      return
    }
    let breaks: CostPrice[] = []
    breaks = costPrices.map((e) => ({
      ...e,
    }))

    if (costPriceStatus === 'pending') {
      return (
        <Box
          sx={{
            transform: 'scale(0.75)',
            marginLeft: '-108px',
          }}
        >
          <Loader
            title='Getting prices'
            type='inline-flex'
            sx={{
              '& *': {
                fontSize: '16px',
              },
            }}
          />
        </Box>
      )
    }

    const groupedBreaks = breaks.reduce<Record<string, CostPrice[]>>((acc, curr) => {
      acc[curr.site] = acc[curr.site] || []
      acc[curr.site].push(curr)
      return acc
    }, {})

    // Step 2: Sort each site's breaks by quantityFrom -> quantityTo
    Object.keys(groupedBreaks).forEach((site) => {
      groupedBreaks[site] = groupedBreaks[site].toSorted((a, b) => a.quantityFrom - b.quantityFrom)
    })

    const groups = Object.entries(groupedBreaks).sort((a, b) =>
      a.toLocaleString().localeCompare(b.toLocaleString()),
    )

    // Step 3: Render grouped and sorted breaks
    if (costPriceStatus === 'fulfilled' || breaks?.length) {
      return groups.map(([site, siteBreaks]) => (
        <Box key={site} mb={2}>
          <Typography variant='h6' color={colors.grey800}>
            {site === 'null' ? 'Other' : site}
          </Typography>
          {siteBreaks.map((breakpoint) => (
            <Stack key={breakpoint.price} spacing={2} direction='row'>
              <Box width='120px'>
                <Typography
                  variant='caption'
                  component='span'
                  fontSize={14}
                  lineHeight={'30px'}
                  fontWeight={400}
                  color={colors.grey700}
                >
                  {getLabel(breakpoint)}
                </Typography>
              </Box>

              <Typography
                variant='body2'
                component='span'
                lineHeight={'30px'}
                fontSize={18}
                fontWeight={700}
                color={colors.grey700}
              >
                {formatPrice(breakpoint.price)}
              </Typography>
            </Stack>
          ))}
        </Box>
      ))
    }
  }, [costPrices, costPriceStatus])

  if (
    (costPrices?.length === 0 && costPriceStatus === 'fulfilled') ||
    (costPrices?.length === 1 &&
      costPrices[0].quantityFrom === null &&
      costPrices[0].quantityTo === null)
  ) {
    return (
      <Typography variant='body2' fontWeight='bold'>
        No cost price available
      </Typography>
    )
  }

  return (
    <Stack>
      <CostPriceButton direction='row' onClick={() => setExpanded(!expanded)}>
        <Typography variant='body2' fontWeight='bold' pb={2}>
          Cost Price
        </Typography>
        <MaterialSymbol
          sx={{
            height: '22px',
            width: '22px',
            transition: 'all 0.2s linear',
            // Flip the icon so it points from up to down if 'expanded' is true
            transform: expanded ? 'scaleY(-1)' : 'scaleY(1)',
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          expand_more
        </MaterialSymbol>
      </CostPriceButton>

      <Collapse in={expanded}>{memo}</Collapse>
    </Stack>
  )
}

export const CostPriceList = withTfoAccess(CostPriceListComponent)
